import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Fade,
  Badge,
  Zoom,
  Stack,
} from "@mui/material";
import { ChevronLeft, ChevronRight, Lightbulb, HelpCircle } from "lucide-react";
import { keyframes } from "@mui/system";
import GameHint from "../../../../components/GameHint";
import { HintModal, HintInline } from "../../../../components/GameHint";
import StreakCounter, {
  StreakModal,
} from "../../../../components/StreakCounter";
import { SEATTLE_TIMES } from "../../../../fcg/wordy/WordyGameEmbed";
import { isFeatureEnabled } from "../../../../utils/featureFlags";

const popIn = keyframes`
  0% { transform: scale(0.8); opacity: 0; }
  40% { transform: scale(1.1); opacity: 0.7; }
  100% { transform: scale(1); opacity: 1; }
`;

// Pulsing animation for the lightbulb
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.15); }
  100% { transform: scale(1); }
`;

// Slight bounce animation
const bounce = keyframes`
  0%, 100% { transform: translateY(0); }
  40% { transform: translateY(-5px); }
  60% { transform: translateY(-3px); }
`;

const GameControls = ({
  currentDate,
  onDateChange,
  showHint,
  onHintToggle,
  wordData,
  correctColor,
  presentColor,
  absentColor,
  streak,
  publicationId,
  isStreakActive,
  isFirstSolver,
  autoShowHowTo = false,
  isEmbedded = false,
  guesses = [], // Add guesses as a prop to track incorrect guesses
  hintUsed = false, // Track if hint has been used already
}) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Create separate states for hint and how-to modals
  const [showHintSection, setShowHintSection] = useState(false);
  const [showInstructions, setShowInstructions] = useState(autoShowHowTo);
  const [showHintIndicator, setShowHintIndicator] = useState(false);
  const [showStreakModal, setShowStreakModal] = useState(false);
  const containerRef = useRef(null);
  const initializedRef = useRef(false);

  // Handle automatic showing of instructions
  useEffect(() => {
    // If this is the first render and autoShowHowTo is true, show instructions
    if (!initializedRef.current) {
      initializedRef.current = true;

      if (autoShowHowTo) {
        setShowInstructions(true);
      } else if (localStorage.getItem("hasSeenHowTo") !== "true") {
        // For first-time users who haven't seen the instructions yet
        setShowInstructions(true);
        localStorage.setItem("hasSeenHowTo", "true");
      }
    }
  }, [autoShowHowTo]);

  // Effect to show hint indicator after 3rd incorrect guess if hint not used
  useEffect(() => {
    // Only show hint indicator if:
    // 1. There are at least 3 guesses
    // 2. The hint hasn't been used (showHintSection is false and no previous click)
    // 3. The game hasn't been won (the 3rd guess wasn't correct)
    // 4. We have a hint available (no theme, has hint)
    if (
      guesses.length >= 3 &&
      !hintUsed &&
      !showHintSection &&
      !wordData.theme &&
      wordData.hint &&
      guesses[guesses.length - 1]?.toUpperCase() !==
        wordData.word?.toUpperCase()
    ) {
      setShowHintIndicator(true);
    } else {
      setShowHintIndicator(false);
    }
  }, [guesses, hintUsed, showHintSection, wordData]);

  // Toggle hint section visibility
  const toggleHintSection = () => {
    const opening = !showHintSection;
    setShowHintSection(opening);
    setShowHintIndicator(false); // Hide indicator when hint is clicked

    // Only mark hint as used when opening the section, not when closing it
    if (opening && !hintUsed) {
      onHintToggle(); // Call the parent component's toggle function to mark hint as used
    }
  };

  // Toggle instructions visibility
  const toggleInstructions = () => {
    setShowInstructions(!showInstructions);
  };

  const toggleStreakModal = () => {
    setShowStreakModal(!showStreakModal);
  };

  // Check if streak counter feature is enabled for this publication
  const isStreakCounterEnabled = isFeatureEnabled(
    "STREAK_COUNTER",
    publicationId,
    isEmbedded
  );

  // Create a completely different layout structure for the controls
  return (
    <>
      {/* Main container with controls */}
      <Box
        ref={containerRef}
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          mb: 2,
          mt: 2,
          position: "relative",
          minHeight: 40,
        }}
      >
        {/* Date navigation on left */}
        <Box sx={{ display: "flex", alignItems: "center", mr: 1, mt: 0 }}>
          <IconButton
            onClick={() => onDateChange(-1)}
            onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
            tabIndex={-1}
          >
            <ChevronLeft />
          </IconButton>
          <Typography sx={{ mx: 0, color: "black" }}>
            {currentDate.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </Typography>
          {currentDate < today && (
            <IconButton
              onClick={() => onDateChange(1)}
              onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
              tabIndex={-1}
            >
              <ChevronRight />
            </IconButton>
          )}
        </Box>

        {/* Controls container to align icons */}
        <Box sx={{ display: "flex", alignItems: "center", mt: 0 }}>
          {/* Hint button (optional) */}
          {!wordData.theme && wordData.hint && (
            <Box
              sx={{
                position: "relative",
                mx: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "1px",
              }}
            >
              {/* Hint indicator - positioned absolutely over the hint button */}
              {showHintIndicator && (
                <Fade in={showHintIndicator}>
                  <Box
                    sx={{
                      position: "absolute",
                      top: -30,
                      left: 0,
                      transform: "translateX(-50%)",
                      backgroundColor: "#FFF7E9",
                      color: "#333",
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 10,
                      fontSize: "0.75rem",
                      fontWeight: "500",
                      whiteSpace: "nowrap",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
                      border: "1px solid rgba(0,0,0,0.1)",
                      zIndex: 5,
                      animation: `${bounce} 1.5s ease infinite`,
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        bottom: -8,
                        left: "50%",
                        marginLeft: -4,
                        borderWidth: 4,
                        borderStyle: "solid",
                        borderColor:
                          "#FFF7E9 transparent transparent transparent",
                      },
                    }}
                  >
                    Need a hint?
                  </Box>
                </Fade>
              )}
              <IconButton
                onClick={toggleHintSection}
                sx={{
                  color: showHintSection
                    ? "black"
                    : showHintIndicator
                    ? "black"
                    : "action.disabled",
                  "&:hover": { color: "black" },
                  transition: "color 0.3s ease",
                  animation: showHintIndicator
                    ? `${pulse} 1.5s ease infinite`
                    : "none",
                  padding: 1,
                }}
              >
                <Lightbulb size={20} />
              </IconButton>
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.65rem",
                  color: "text.secondary",
                  lineHeight: 1,
                  mt: -0.5,
                }}
              >
                Hint
              </Typography>
            </Box>
          )}

          {/* How To button - now directly toggles the instructions panel */}
          <Box
            sx={{
              position: "relative",
              mx: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "1px",
            }}
          >
            <IconButton
              onClick={toggleInstructions}
              sx={{
                color: showInstructions ? "black" : "action.disabled",
                "&:hover": { color: "black" },
                transition: "color 0.3s ease",
                padding: 1,
              }}
            >
              <HelpCircle size={20} />
            </IconButton>
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.65rem",
                color: "text.secondary",
                lineHeight: 1,
                mt: -0.5,
              }}
            >
              Help
            </Typography>
          </Box>
        </Box>
        {/* Streak Counter - only show if feature is enabled */}
        {isStreakCounterEnabled && (
          <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
            <StreakCounter
              streak={streak}
              isActive={isStreakActive}
              isFirstSolver={isFirstSolver}
              onClick={toggleStreakModal}
            />
          </Box>
        )}
      </Box>

      {/* Hint Section - Inline version, similar to instructions */}
      {!wordData.theme && wordData.hint && (
        <GameHint
          theme={false}
          hint={wordData.hint}
          url={wordData.url}
          open={showHintSection}
          onClose={toggleHintSection}
          isEmbedded={isEmbedded}
        />
      )}

      {/* Game Instructions - now using modal instead of inline */}
      <GameHint
        theme={true}
        open={showInstructions}
        hint={wordData.hint}
        onClose={toggleInstructions}
        correctColor={correctColor}
        presentColor={presentColor}
        absentColor={absentColor}
        isEmbedded={isEmbedded}
      />

      <StreakModal
        open={showStreakModal}
        onClose={toggleStreakModal}
        streak={streak}
        isActive={isStreakActive}
        isEmbedded={isEmbedded}
      />
    </>
  );
};

export default GameControls;
